// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { Alert,Box, Grid, Typography, Tabs, Tab } from "@mui/material";
import Info from "@mui/icons-material/Info";
import { Container } from "@mui/system";
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { useEffect, useState } from "react";
import SharableLink from "../components/certificate/SharableLink";
import { DataLoader } from "../components/common/DataLoader";
import CertificationsList from "../components/lists/CertificationsList";
import { contactDetails } from "../config/constant";
import { getCertifications } from "../slices/certificationSlice/certificationsHistory";
import { getAwards } from "../slices/certificationSlice/awards";
import { RootState, useAppDispatch, useAppSelector } from "../slices/store";
import { GetCertificationsRequestResponse, AwardsPayload } from "../utils/types";
import Loading from "./Loading";
import AwardsList from "../components/lists/AwardsList";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface TabTitleProps {
  name: string;
  value?: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const TabTitle: React.FC<TabTitleProps> = ({ name, value = 0 }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {name}
      {value > 0 && (
        <Box
          sx={{
            display: "inline-block",
            marginLeft: "8px",
            backgroundColor: theme => theme.palette.grey[300],
            color: theme => theme.palette.grey[800],
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            textAlign: "center",
            lineHeight: "24px",
            fontSize: "12px",
          }}
        >
          {value}
        </Box>
      )}
    </Box>
  );
}

const Certifications = () => {
  const [value, setValue] = useState(0);
  const dispatch = useAppDispatch();
  const contactState = useAppSelector((state: RootState) => state.contact);
  const authState = useAppSelector((state: RootState) => state.auth);
  const certificationState = useAppSelector(
    (state: RootState) => state.certifications
  );
  const awardState = useAppSelector(
    (state: RootState) => state.awards
  );

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (authState.status === "success" && contactState.state === "success") {
      dispatch(getCertifications(contactState.response.certificationId));
      dispatch(getAwards(contactState.response.email));
    }
  }, [contactState.state]);

  const certificationData: GetCertificationsRequestResponse =
    certificationState.response;

  const awardData: AwardsPayload[] =
    awardState.response;

  return (
    <>
      {contactState.backgroundProcess ? (
        <Loading message={contactState.backgroundProcessMessage} />
      ) : (
        <Container>
          <Grid container columnSpacing="10" mb={10}>
            <Grid
              item
              md={8}
              xs={12}
              sx={{
                mt: 5,
                pt: 10,
                pb: 5,
              }}
            >
              <Typography 
                sx={{ 
                  textTransform: 'uppercase', 
                  fontWeight: 'bold',
                  fontSize: '1.2rem'
                }}
                component="h1" variant="h4" align="left">
                  My Certifications
              </Typography>
              <Typography variant="body1" align="left" component="p" mt={1}>
                You have obtained the following certifications and awards.
              </Typography>
            </Grid>
            <Grid
              item
              md={12}
              display="flex"
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              {certificationData.awardededCertifications.length !== 0 && (
                <SharableLink
                  certificationId={contactState.response.certificationId}
                />
              )}
            </Grid>
            <Grid item md={12}>
              {certificationState.backgroundProcess ? (
                <DataLoader
                  message={certificationState.backgroundProcessMessage}
                />
              ) : (
                <Box sx={{ width: '100%', marginTop: '1rem' }}>
                  <Box sx={{ width: '100%', bgcolor: '#f7f7f7' }}>
                    <Tabs value={value} onChange={handleChange} indicatorColor="secondary">
                      <Tab label={<TabTitle name="Certifications" value={certificationData.awardededCertifications.length}/>} value={0} />
                      <Tab label={<TabTitle name="Awards" value={awardData.length}/>} value={1}/>
                      <Tab label={<TabTitle name="Expired" value={certificationData.expiredCertifications.length} />} value={2} />
                    </Tabs>
                  </Box>
                  <CustomTabPanel value={value} index={0}>
                    <CertificationsList
                      certificationData={
                        certificationData.awardededCertifications
                      }
                      isOnlyExpiredCertificates={false}
                    />
                  </CustomTabPanel>
                  <CustomTabPanel value={value} index={1}>
                    {awardData.length !== 0 ? (
                      <>
                        <AwardsList certificationData={awardData} />
                      </>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <img
                          src="/no-data.svg"
                          alt="404"
                          height={200}
                          style={{ marginTop: 50 }}
                        />

                        <Typography variant="h6" sx={{ color: "gray", mt: 5 }}>
                          No data to show
                        </Typography>
                      </Box>
                    )}
                  </CustomTabPanel>
                  <CustomTabPanel value={value} index={2}>
                  <Alert
                    icon={<Info sx={{ color: theme => theme.palette.secondary.main }} />}
                    severity="info"
                    sx={{
                      paddingX: 2,
                      paddingY: 1,
                      color: 'black',
                      backgroundColor: 'transparent',
                      borderRadius: 2,
                      borderWidth: 1,
                      borderStyle: 'solid', 
                      borderColor: theme => theme.palette.secondary.main,
                    }}
                  >
                    <Typography variant="body1" align="left" component="p" mt={1}>
                      Certain certifications, such as Sales, Solutions Architect, and Choreo certifications, come with an expiration date. To maintain and update your credentials, simply complete the latest version of the certification.
                    </Typography>
                  </Alert>
                  <CertificationsList
                    certificationData={
                      certificationData.expiredCertifications
                    }
                    isOnlyExpiredCertificates={true}
                  />
                  </CustomTabPanel>
                </Box>
              )}
            </Grid>
          </Grid>
        </Container>
      )}
    </>
  );
};

export default Certifications;
